import { CalendarEventResponseStatus } from '@griply/types';
import {
  CalendarEventGuestStatusColor,
  getCalendarEventGuestStatusColor,
} from '@griply/utils';
import { focusOutline } from 'shared/assets/styles';
import { typographyToCss } from 'shared/lib/mui';
import styled, { DefaultTheme } from 'styled-components';

const statusColor = (
  theme: DefaultTheme,
  status: CalendarEventResponseStatus,
): string => {
  const colors: Record<CalendarEventGuestStatusColor, string> = {
    [CalendarEventGuestStatusColor.Success]: theme.palette.success.main,
    [CalendarEventGuestStatusColor.Error]: theme.palette.error.main,
    [CalendarEventGuestStatusColor.Warning]: theme.palette.warning.main,
    [CalendarEventGuestStatusColor.Neutral]: theme.palette.grey[400],
  };
  return colors[getCalendarEventGuestStatusColor(status)];
};

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  width: 100%;
  min-width: 0;
`;

// the suggestion popup anchors to the field
export const InputAnchor = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

export const GuestList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const GuestRow = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
  min-height: 2.8rem;
`;

export const GuestName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OrganizerBadge = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};

  flex-shrink: 0;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

// hidden rather than unmounted so revealing it never reflows the row
export const RemoveButton = styled.span`
  display: flex;
  visibility: hidden;

  ${GuestRow}:hover &,
  ${GuestRow}:focus-within & {
    visibility: visible;
  }

  // a touch device has no hover to reveal it with
  @media (hover: none) {
    visibility: visible;
  }
`;

export const MoreButton = styled.button`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  align-self: flex-start;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.secondary};

  &:focus-visible {
    ${focusOutline};
  }
`;

export const ErrorText = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  color: ${({ theme }) => theme.palette.error.main};
`;

export const StatusDot = styled.span<{
  $status: CalendarEventResponseStatus;
}>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme, $status }) => statusColor(theme, $status)};
`;
